import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

const loadingTips = [
  "Tuning the frequencies...",
  "Loading the vibes...",
  "Preparing your experience...",
  "Setting the mood...",
  "Curating the sound...",
  "Syncing the beats...",
  "Warming up the speakers...",
  "Loading musical magic...",
];

export const FIRST_LOAD_DEADLINE_MS = 6000;

interface FirstTimeLoaderProps {
  onComplete?: () => void;
  artistName?: string;
}

function FirstTimeLoader({ onComplete, artistName = "Truevined" }: FirstTimeLoaderProps) {
  const [progress, setProgress] = useState(0);
  const [currentTip, setCurrentTip] = useState(loadingTips[0]);
  const tipStageRef = useRef(-1);
  const lastTipRef = useRef(-1);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * loadingTips.length);
    lastTipRef.current = randomIndex;
    setCurrentTip(loadingTips[randomIndex]);
  }, []);

  useEffect(() => {
    const duration = 2500;
    const interval = 50;
    const increment = 100 / (duration / interval);
    const startedAt = Date.now();
    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      window.clearInterval(timer);
      window.clearTimeout(deadline);
      setProgress(100);
      window.setTimeout(() => onComplete?.(), 300);
    };

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const stepped = Math.min((elapsed / interval) * increment, 100);

      setProgress((prevProgress) => Math.max(prevProgress, stepped));

      const nextStage = Math.min(Math.floor(stepped / 25), 3);
      if (nextStage !== tipStageRef.current) {
        tipStageRef.current = nextStage;
        const availableTips = loadingTips.filter((_, index) => index !== lastTipRef.current);
        const pick = Math.floor(Math.random() * availableTips.length);
        lastTipRef.current = loadingTips.indexOf(availableTips[pick]);
        setCurrentTip(availableTips[pick]);
      }

      if (stepped >= 100) finish();
    }, interval);

    const deadline = window.setTimeout(finish, FIRST_LOAD_DEADLINE_MS);

    return () => {
      window.clearInterval(timer);
      window.clearTimeout(deadline);
    };
  }, [onComplete]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "var(--backgroundColor, #0a0a0a)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: "3rem", md: "2rem" },
          padding: "2rem",
          maxWidth: "500px",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Bellefair, serif",
            fontSize: { xs: "4rem", md: "5rem" },
            background: "linear-gradient(45deg, var(--logoColor, #d4a574), var(--logoColor2, #b8956c))",
            backgroundSize: "400% 400%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: "gradientShift 4s ease-in-out infinite",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            textAlign: "center",
          }}
        >
          {artistName}
        </Typography>

        <Box
          sx={{
            width: "100%",
            maxWidth: "320px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "4px",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${progress}%`,
                background: "linear-gradient(90deg, var(--logoColor2, #b8956c) 0%, var(--logoColor, #d4a574) 50%, var(--logoColor2, #b8956c) 100%)",
                borderRadius: "4px",
                transition: "width 0.1s ease-out",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                  animation: "shine 1.5s ease-in-out infinite",
                },
              }}
            />
          </Box>

          <Typography
            sx={{
              color: "var(--logoColor, #d4a574)",
              fontSize: { xs: "1.75rem", md: "1.25rem" },
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              letterSpacing: "0.1em",
            }}
          >
            {Math.floor(progress)}%
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "var(--logoColor, #d4a574)",
            fontSize: { xs: "1.5rem", md: "1rem" },
            fontFamily: "Poppins, sans-serif",
            fontWeight: 400,
            opacity: 0.7,
            letterSpacing: "0.05em",
            animation: "tipFade 0.5s ease-in-out",
            minHeight: { xs: "2rem", md: "1.5rem" },
            textAlign: "center",
          }}
          key={currentTip}
        >
          {currentTip}
        </Typography>
      </Box>

      <style>
        {`
          @keyframes gradientShift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          @keyframes shine {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes tipFade {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 0.7;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </Box>
  );
}

export default FirstTimeLoader;
